import { useRef, useState } from "react";
import clsx from "clsx";

const MAX_BYTES = 4 * 1024 * 1024;
const ALLOWED = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export type CvPayload = {
  name: string;
  type: string;
  size: number;
  base64: string;
};

export function FileDrop({
  value,
  onChange,
  error,
}: {
  value: CvPayload | undefined;
  onChange: (v: CvPayload | undefined) => void;
  error?: string;
}) {
  const [drag, setDrag] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handle(file: File) {
    setLocalErr(null);
    if (!ALLOWED.includes(file.type) && !/\.(pdf|docx?|DOCX?)$/.test(file.name)) {
      setLocalErr("Only PDF or Word documents are accepted.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalErr("File must be 4 MB or smaller.");
      return;
    }
    const base64 = await fileToBase64(file);
    onChange({ name: file.name, type: file.type, size: file.size, base64 });
  }

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          const file = e.dataTransfer.files?.[0];
          if (file) void handle(file);
        }}
        className={clsx(
          "rounded-xl2 border-2 border-dashed p-8 text-center transition cursor-pointer",
          drag
            ? "border-brand-teal bg-brand-teal/5"
            : "border-brand-line bg-white hover:border-brand-teal/60",
        )}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handle(f);
          }}
        />
        {value ? (
          <div className="space-y-2">
            <div className="text-[15px] font-semibold text-brand-ink">{value.name}</div>
            <div className="text-[13px] text-brand-mute">
              {(value.size / 1024).toFixed(0)} KB, ready to upload
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(undefined);
                if (inputRef.current) inputRef.current.value = "";
              }}
              className="text-[13px] text-brand-red font-semibold underline"
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-[15px] font-semibold text-brand-ink">
              Drag and drop your CV here
            </div>
            <div className="text-[13px] text-brand-mute">
              or click to choose a file. PDF or Word, up to 4 MB.
            </div>
          </div>
        )}
      </div>
      {(localErr || error) && (
        <p className="ac-error">{localErr || error}</p>
      )}
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1] ?? "";
      resolve(base64);
    };
    reader.readAsDataURL(file);
  });
}
